import React, { useState, useMemo } from 'react';
import { AssessmentPoint } from '../../types';
import { PageHeader, StatsCards } from '../../components/Common';
import { StatCard } from '../../components/Common/StatsCards';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import {
  useAssessmentPoints,
  useCreateAssessmentPoint,
  useUpdateAssessmentPoint,
  useDeleteAssessmentPoint
} from '../../hooks/useAssessmentPoints';
import {
  CandidateEvaluation,
  buildDefaultPoints,
  getStatusFromPercentage,
  groupByCategory,
  printAssessmentSheet,
  mapEvaluationForExport,
  mapPointForExport,
} from './evaluationTypes';
import EvaluationList from './EvaluationList';
import EvaluationForm from './EvaluationForm';
import EvaluationDetails from './EvaluationDetails';
import AssessmentPointsTab from './AssessmentPointsTab';

const INITIAL_EVALUATIONS: CandidateEvaluation[] = [
  {
    id: '1', candidateId: '1', candidateName: 'Ahmed Hassan', jobTitle: 'Chief Engineer',
    evaluationDate: '2024-01-20', evaluatorName: 'Captain Smith',
    totalScore: 36, maxScore: 40, percentage: 90, status: 'excellent',
    ratings: [], notes: 'Excellent technical skills and experience',
  },
  {
    id: '2', candidateId: '2', candidateName: 'Sarah Mohamed', jobTitle: 'Second Officer',
    evaluationDate: '2024-01-22', evaluatorName: 'HR Manager',
    totalScore: 28, maxScore: 40, percentage: 70, status: 'average',
    ratings: [], notes: 'Good communication skills, needs more experience',
  },
  {
    id: '3', candidateId: '3', candidateName: 'Mohamed Ali', jobTitle: 'Able Seaman',
    evaluationDate: '2024-01-25', evaluatorName: 'Chief Officer',
    totalScore: 20, maxScore: 40, percentage: 50, status: 'unsatisfactory',
    ratings: [], notes: 'Below expectations, not suitable at this time',
  },
];

const CandidatesEvaluation: React.FC = () => {
  const { data: assessmentPoints = [], isLoading: pointsLoading, error: pointsError } = useAssessmentPoints();
  const createPointMutation = useCreateAssessmentPoint();
  const updatePointMutation = useUpdateAssessmentPoint();
  const deletePointMutation = useDeleteAssessmentPoint();

  const effectiveCriteria = useMemo(() => {
    return assessmentPoints.length > 0 ? assessmentPoints : buildDefaultPoints();
  }, [assessmentPoints]);

  const [activeTab, setActiveTab] = useState<'evaluations' | 'points'>('evaluations');
  const [evaluations, setEvaluations] = useState<CandidateEvaluation[]>(INITIAL_EVALUATIONS);
  const [showEvaluationForm, setShowEvaluationForm] = useState(false);
  const [selectedEvaluation, setSelectedEvaluation] = useState<CandidateEvaluation | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const [showPointsForm, setShowPointsForm] = useState(false);
  const [editingPoint, setEditingPoint] = useState<AssessmentPoint | undefined>();
  const [pointsSearchTerm, setPointsSearchTerm] = useState('');

  const [candidateName, setCandidateName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [evaluatorName, setEvaluatorName] = useState('');
  const [ratings, setRatings] = useState<{ [key: string]: number }>({});
  const [notes, setNotes] = useState('');

  const filteredEvaluations = useMemo(() => {
    return evaluations.filter(evaluation => {
      const matchesSearch = !searchTerm || (
        evaluation.candidateName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        evaluation.jobTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
        evaluation.evaluatorName.toLowerCase().includes(searchTerm.toLowerCase())
      );
      const matchesStatus = statusFilter === 'all' || evaluation.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [evaluations, searchTerm, statusFilter]);

  const filteredPoints = useMemo(() => {
    return assessmentPoints.filter(point => {
      return !pointsSearchTerm || (
        point.name.toLowerCase().includes(pointsSearchTerm.toLowerCase()) ||
        point.description?.toLowerCase().includes(pointsSearchTerm.toLowerCase()) ||
        point.category.toLowerCase().includes(pointsSearchTerm.toLowerCase())
      );
    });
  }, [assessmentPoints, pointsSearchTerm]);

  const groupedCriteria = useMemo(() => {
    return groupByCategory(effectiveCriteria.filter(p => p.isActive));
  }, [effectiveCriteria]);

  const handleAddEvaluation = () => {
    setCandidateName('');
    setJobTitle('');
    setEvaluatorName('');
    setRatings({});
    setNotes('');
    setSelectedEvaluation(null);
    setShowEvaluationForm(true);
  };

  const handleViewEvaluation = (evaluation: CandidateEvaluation) => {
    setSelectedEvaluation(evaluation);
    setShowEvaluationForm(false);
  };

  const handleRatingChange = (pointId: string, score: number) => {
    setRatings(prev => ({ ...prev, [pointId]: score }));
  };

  const handleSubmitEvaluation = () => {
    const activeCriteria = effectiveCriteria.filter(p => p.isActive);
    const totalScore = activeCriteria.reduce((sum, point) => sum + (ratings[point.id] || 0), 0);
    const maxScore = activeCriteria.length * 4;
    const percentage = maxScore > 0 ? (totalScore / maxScore) * 100 : 0;
    const status = getStatusFromPercentage(percentage);

    const newEvaluation: CandidateEvaluation = {
      id: Date.now().toString(),
      candidateId: Date.now().toString(),
      candidateName, jobTitle,
      evaluationDate: new Date().toISOString().split('T')[0],
      evaluatorName, totalScore, maxScore, percentage, status,
      ratings: [],
      ratingMap: { ...ratings },
      notes,
    };

    setEvaluations([...evaluations, newEvaluation]);
    setShowEvaluationForm(false);
  };

  const handleExport = () => {
    exportToExcel(filteredEvaluations.map(mapEvaluationForExport), `candidates_evaluation_${new Date().toISOString().split('T')[0]}`);
  };

  const handlePrint = () => {
    printAsExcel(filteredEvaluations.map(mapEvaluationForExport), 'Candidates Evaluation');
  };

  const handlePrintAssessmentSheet = () => {
    if (selectedEvaluation) {
      printAssessmentSheet(selectedEvaluation, effectiveCriteria.filter(p => p.isActive));
    }
  };

  const handleAddPoint = () => { setEditingPoint(undefined); setShowPointsForm(true); };
  const handleEditPoint = (point: AssessmentPoint) => { setEditingPoint(point); setShowPointsForm(true); };
  const handleDeletePoint = async (point: AssessmentPoint) => {
    if (window.confirm(`Are you sure you want to delete the assessment point "${point.name}"?`)) {
      await deletePointMutation.execute(point.documentId || point.id);
    }
  };

  const handlePointFormSubmit = async (pointData: Partial<AssessmentPoint>) => {
    if (editingPoint) {
      await updatePointMutation.execute({
        id: editingPoint.documentId || editingPoint.id,
        data: { ...pointData, documentId: editingPoint.documentId }
      });
    } else {
      await createPointMutation.execute(pointData);
    }
    setShowPointsForm(false);
    setEditingPoint(undefined);
  };

  const handlePointFormCancel = () => { setShowPointsForm(false); setEditingPoint(undefined); };

  const handleExportPoints = () => {
    exportToExcel(filteredPoints.map(mapPointForExport), `assessment_points_${new Date().toISOString().split('T')[0]}`);
  };

  const handlePrintPoints = () => {
    printAsExcel(filteredPoints.map(mapPointForExport), 'Assessment Points');
  };

  const evaluationStats: StatCard[] = [
    { label: 'Excellent', value: evaluations.filter(e => e.status === 'excellent').length, color: 'green' },
    { label: 'Good', value: evaluations.filter(e => e.status === 'good').length, color: 'blue' },
    { label: 'Average', value: evaluations.filter(e => e.status === 'average').length, color: 'yellow' },
    { label: 'Unsatisfactory', value: evaluations.filter(e => e.status === 'unsatisfactory').length, color: 'red' },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Candidates Evaluation"
        description="Evaluate job candidates based on assessment points and criteria."
        actions={[
          { label: 'Export', onClick: activeTab === 'evaluations' ? handleExport : handleExportPoints, variant: 'success' },
          { label: 'Print', onClick: activeTab === 'evaluations' ? handlePrint : handlePrintPoints, variant: 'info' },
        ]}
      />

      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('evaluations')}
            className={`${
              activeTab === 'evaluations'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200`}
          >
            Evaluations
          </button>
          <button
            onClick={() => setActiveTab('points')}
            className={`${
              activeTab === 'points'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200`}
          >
            Assessment Points
          </button>
        </nav>
      </div>

      {activeTab === 'evaluations' && (
        <>
          <StatsCards stats={evaluationStats} />

          {!showEvaluationForm && !selectedEvaluation && (
            <EvaluationList
              evaluations={evaluations}
              filteredEvaluations={filteredEvaluations}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
              onAdd={handleAddEvaluation}
              onView={handleViewEvaluation}
              onExport={handleExport}
              onPrint={handlePrint}
            />
          )}

          {showEvaluationForm && (
            <EvaluationForm
              candidateName={candidateName} setCandidateName={setCandidateName}
              jobTitle={jobTitle} setJobTitle={setJobTitle}
              evaluatorName={evaluatorName} setEvaluatorName={setEvaluatorName}
              ratings={ratings} onRatingChange={handleRatingChange}
              notes={notes} setNotes={setNotes}
              groupedCriteria={groupedCriteria}
              onSubmit={handleSubmitEvaluation}
              onCancel={() => setShowEvaluationForm(false)}
            />
          )}

          {selectedEvaluation && !showEvaluationForm && (
            <EvaluationDetails
              evaluation={selectedEvaluation}
              groupedCriteria={groupedCriteria}
              onPrintAssessmentSheet={handlePrintAssessmentSheet}
              onClose={() => setSelectedEvaluation(null)}
            />
          )}
        </>
      )}

      {activeTab === 'points' && (
        <AssessmentPointsTab
          assessmentPoints={assessmentPoints}
          filteredPoints={filteredPoints}
          pointsLoading={pointsLoading}
          pointsError={pointsError}
          pointsSearchTerm={pointsSearchTerm}
          onPointsSearchChange={setPointsSearchTerm}
          showPointsForm={showPointsForm}
          editingPoint={editingPoint}
          onAddPoint={handleAddPoint}
          onEditPoint={handleEditPoint}
          onDeletePoint={handleDeletePoint}
          onPointFormSubmit={handlePointFormSubmit}
          onPointFormCancel={handlePointFormCancel}
          onExport={handleExportPoints}
          onPrint={handlePrintPoints}
        />
      )}
    </div>
  );
};

export default CandidatesEvaluation;
